/**
 * @module frontend/theme/tokens
 * @description The application's semantic colour roles, the custom property
 * each role is published under, and the categorical chart ramp.
 *
 * The record is the one place a colour value is written. Tailwind reads its
 * theme from CSS, so `scripts/emitTheme.ts` renders this record into
 * `src/theme/tokens.css`, which is what turns a role into the `ui` utilities
 * and the `--color-ui-*` properties on `:root`. The module imports nothing,
 * which is what lets that script load it under Node with no bundler.
 *
 * A wrong hex or a duplicated key still compiles and still renders a theme
 * variable: the build cannot tell a wrong colour from a right one.
 * `src/test/conventions/contrast.test.ts` is what catches a value that crosses
 * a WCAG bar, and `src/test/conventions/theme-emission.test.ts` is what catches
 * a role the rendered theme does not declare. A green build is not evidence the
 * tokens are right; those two guards are.
 */

/** A CSS hex colour. Every value declared here is six digits. */
export type Hex = `#${string}`

/**
 * Graphite values, keyed by the semantic role each one plays.
 *
 * The keys are flat and hyphenated because each one becomes a single theme
 * variable, `--color-ui-<key>`, and so a single utility name: `text-ui-text`,
 * `text-ui-text-muted` and `text-ui-text-faint` are the names call sites write.
 */
export const TOKENS = {
  canvas: '#0e1014',
  surface: '#14171d',
  raised: '#1b1f27',
  hairline: '#2a303b',
  text: '#e6e8ef',
  'text-muted': '#9aa1ad',
  'text-faint': '#929baa',
  now: '#38d1e3',
  memory: '#9b8cff',
  success: '#42d392',
  warning: '#f5b74e',
  error: '#ff6b6b',
  info: '#7fb2ff',
  boundary: '#737d8e',
  'on-accent': '#0e1014',
} as const satisfies Record<string, Hex>

/** The semantic role a `ui-*` utility names. */
export type TokenName = keyof typeof TOKENS

/** Both palettes share exactly the same semantic roles. */
export type ThemeMode = 'light' | 'dark'
export type Palette = Readonly<Record<TokenName, Hex>>

export const LIGHT_TOKENS = {
  canvas: '#f4f6f9',
  surface: '#ffffff',
  raised: '#e9edf3',
  hairline: '#ced5df',
  text: '#202733',
  'text-muted': '#515d6e',
  'text-faint': '#5d6877',
  now: '#006f80',
  memory: '#6750c4',
  success: '#146c43',
  warning: '#8a5700',
  error: '#b83232',
  info: '#285fb5',
  boundary: '#7b8594',
  'on-accent': '#ffffff',
} as const satisfies Palette

export const PALETTES = { dark: TOKENS, light: LIGHT_TOKENS } as const

/** Every role, in the record's order. */
export const TOKEN_NAMES = Object.keys(TOKENS) as readonly TokenName[]

/**
 * The one colour family the theme declares: utilities read `bg-ui-<role>`,
 * and the properties sit in Tailwind's `--color-*` namespace under it.
 */
export const TOKEN_FAMILY = 'ui'

/** The custom property a role is published under on `:root`. */
export type TokenVariable = `--color-${typeof TOKEN_FAMILY}-${TokenName}`

/** `'now'` → `'--color-ui-now'`. */
export function tokenVariable(name: TokenName): TokenVariable {
  return `--color-${TOKEN_FAMILY}-${name}`
}

/**
 * `'now'` → `'var(--color-ui-now)'`: a role as a CSS colour value, for an
 * inline style that has no utility form. The property holds the whole colour,
 * so it is used bare rather than wrapped in `rgb()`.
 */
export function tokenColour(name: TokenName): `var(${TokenVariable})` {
  return `var(${tokenVariable(name)})`
}

/** CSS consumers update without a React render, including SVG attributes. */
export const TOKEN_COLOURS = Object.fromEntries(
  TOKEN_NAMES.map((name) => [name, tokenColour(name)])
) as Readonly<Record<TokenName, ReturnType<typeof tokenColour>>>

/**
 * The categorical series ramp, in the order a chart assigns it by index.
 *
 * Design output of the `dataviz` palette method rather than values chosen
 * here, and held in that order: two pins decide whether a value belongs, and
 * `src/components/charts/__tests__/chartPalette.test.ts` is what enforces them.
 * Every entry clears 3:1 against {@link TOKENS}.surface, the non-text bar a
 * fill has to meet to be seen at all, and every pair sits at least 30° apart in
 * HSL hue, which is what stops two adjacent slices reading as one. The first
 * two are `now` and `memory`: the accents already carry those meanings, so a
 * series starting at the live accent reads as the same system rather than a
 * second identity.
 *
 * A series never reads as a status, so the four status roles stay out of the
 * ramp and the six values after `memory` are placed clear of their hues.
 */
export const CHART_CATEGORICAL: readonly Hex[] = [
  '#38d1e3',
  '#9b8cff',
  '#b64b20',
  '#619f38',
  '#a346ce',
  '#96862c',
  '#d85aa4',
  '#3d8f53',
] as const

/** Matching slots retain categorical identity across modes. */
export const LIGHT_CHART_CATEGORICAL: readonly Hex[] = [
  '#006f80',
  '#6750c4',
  '#a6441d',
  '#4e812d',
  '#983bbd',
  '#807225',
  '#ad4582',
  '#307342',
] as const

export const CHART_PALETTES = {
  dark: CHART_CATEGORICAL,
  light: LIGHT_CHART_CATEGORICAL,
} as const

export const CHART_COLOURS = CHART_CATEGORICAL.map(
  (_, index) => `var(--chart-series-${index})`
)

/** Pick a passing foreground for an opaque chart tile in the active palette. */
export function chartLabelColour(fill: Hex, mode: ThemeMode): Hex {
  const luminance = (hex: Hex) => {
    const [r, g, b] = rgbTriplet(hex)
      .split(' ')
      .map(Number)
      .map((channel) => {
        const c = channel / 255
        return c <= 0.04045 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4
      })
    return 0.2126 * r + 0.7152 * g + 0.0722 * b
  }
  const contrast = (a: Hex, b: Hex) => {
    const values = [luminance(a), luminance(b)].sort((a, b) => b - a)
    return (values[0] + 0.05) / (values[1] + 0.05)
  }
  const palette = PALETTES[mode]
  const candidates = [palette.text, TOKENS.canvas, LIGHT_TOKENS.surface]
  return candidates.reduce((best, value) =>
    contrast(value, fill) > contrast(best, fill) ? value : best
  )
}

/**
 * `'#14171d'` → `'20 23 29'`, the channel form an `rgb(r g b / alpha)` string
 * needs where a module builds a translucent colour from a role.
 *
 * Throws on anything but six hex digits: a shorthand or malformed value would
 * otherwise be sliced into three plausible-looking channels and paint a colour
 * nobody chose.
 */
export function rgbTriplet(hex: Hex): string {
  const digits = hex.slice(1)
  if (!/^[0-9a-fA-F]{6}$/.test(digits)) {
    throw new Error(`expected a six-digit hex colour, got '${hex}'`)
  }
  const red = parseInt(digits.slice(0, 2), 16)
  const green = parseInt(digits.slice(2, 4), 16)
  const blue = parseInt(digits.slice(4, 6), 16)
  return `${red} ${green} ${blue}`
}
